if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.swiperInstance = null;
      this.zoomSwiperInstance = null;
      this.zoomModal = null;
      this._handleKeyDown = null;
      this._zoomPanAbort = null;
    }

    disconnectedCallback() {
      this.zoomModal?.remove();
    }

    connectedCallback() {
      if (typeof Swiper !== 'undefined') {
        this.init();
      } else {
        const checkSwiper = setInterval(() => {
          if (typeof Swiper !== 'undefined') {
            clearInterval(checkSwiper);
            this.init();
          }
        }, 100);
      }
    }

    init() {
      this.zoomModal = this.querySelector('#ZoomModal');
      if (this.zoomModal) document.body.appendChild(this.zoomModal);

      this.initMainSwiper();
      this._initGalleryVideoControls(this);
      this._initGalleryVideoControls(this.zoomModal);
      this.bindEvents();
    }

    initMainSwiper() {
      const swiperContainer = this.querySelector('.product-gallery-swiper');
      if (!swiperContainer) return;

      this.swiperInstance = new Swiper(swiperContainer, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: false,
        autoHeight: false,
        pagination: {
          el: '.swiper-pagination-product-gallery',
          clickable: true,
          type: 'bullets',
        },
        navigation: {
          nextEl: '#gallery-button-next',
          prevEl: '#gallery-button-prev',
        },
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        watchSlidesProgress: true,
        on: {
          afterInit: (swiper) => {
            requestAnimationFrame(() => this._playSlideMedia(swiper.activeIndex));
          },
          slideChange: (swiper) => {
            this._playSlideMedia(swiper.activeIndex);
          },
        },
      });
    }

    _initGalleryVideoControls(container) {
      if (!container) return;
      container.querySelectorAll('[data-gallery-video]').forEach((wrapper) => {
        const video = wrapper.querySelector('video');
        if (!video) return;
        const playBtn = wrapper.querySelector('.gallery-video-play');
        const pauseBtn = wrapper.querySelector('.gallery-video-pause');

        const sync = () => {
          playBtn?.classList.toggle('hidden', !video.paused);
          pauseBtn?.classList.toggle('hidden', video.paused);
        };

        video.addEventListener('play', sync);
        video.addEventListener('pause', sync);
        sync();

        playBtn?.addEventListener('click', (e) => { e.stopPropagation(); video.play().catch(() => {}); });
        pauseBtn?.addEventListener('click', (e) => { e.stopPropagation(); video.pause(); });
      });
    }

    _playSlideMedia(index) {
      const swiper = this.swiperInstance;
      if (!swiper) return;
      if (this.zoomSwiperInstance) return;
      const slide = swiper.slides[index];
      if (!slide) return;

      // Native gallery video (no deferred)
      const galleryVideo = slide.querySelector('[data-gallery-video] video');
      if (galleryVideo) {
        window.pauseAllMedia();
        galleryVideo.play()?.catch(() => {});
        return;
      }

      // Deferred media (external_video, model)
      const deferredMedia = slide.querySelector('deferred-media');
      if (!deferredMedia) {
        window.pauseAllMedia();
        return;
      }
      if (!deferredMedia.getAttribute('loaded')) {
        deferredMedia.loadContent(false);
      }
      requestAnimationFrame(() => {
        const video = deferredMedia.querySelector('video');
        if (video) video.play()?.catch(() => {});
      });
    }

    _playZoomSlideMedia(index) {
      if (!this.zoomSwiperInstance) return;
      const slide = this.zoomSwiperInstance.slides[index];
      if (!slide) return;

      // Native video via gallery-video snippet
      const galleryVideo = slide.querySelector('[data-gallery-video] video');
      if (galleryVideo) {
        window.pauseAllMedia();
        galleryVideo.play()?.catch(() => {});
        return;
      }

      // External video — deferred-media (iframe)
      const deferredMedia = slide.querySelector('deferred-media');
      if (!deferredMedia) return;
      if (!deferredMedia.getAttribute('loaded')) {
        deferredMedia.loadContent(false);
      }
    }

    bindEvents() {
      this.addEventListener('click', (e) => {
        const target = e.target;

        // Zoom Trigger
        const zoomTrigger = target.closest('.gallery-zoom-trigger');
        if (zoomTrigger) {
          e.preventDefault();
          e.stopPropagation();
          const currentIndex = this.swiperInstance ? this.swiperInstance.activeIndex : 0;
          this.openZoom(currentIndex);
          return;
        }
      });

      // Close Zoom
      this.zoomModal?.querySelector('#CloseZoomModal')?.addEventListener('click', () => this.closeZoom());
    }

    // --- ZOOM ---
    openZoom(index) {
      const modal = this.zoomModal;
      const zoomContainer = modal?.querySelector('.zoom-gallery-swiper');
      if (!modal || !zoomContainer) return;

      window.pauseAllMedia();

      modal.classList.remove('hidden');
      document.body.classList.add('overflow-hidden');

      if (this.zoomSwiperInstance) {
        this.zoomSwiperInstance.destroy(true, true);
        this.zoomSwiperInstance = null;
      }

      this.zoomSwiperInstance = new Swiper(zoomContainer, {
        slidesPerView: 1,
        spaceBetween: 0,
        initialSlide: index,
        navigation: {
          nextEl: '#zoom-button-next, #zoom-button-next-mobile',
          prevEl: '#zoom-button-prev, #zoom-button-prev-mobile',
        },
        on: {
          slideChange: (swiper) => {
            if (this.swiperInstance) {
              this.swiperInstance.slideTo(swiper.activeIndex, 0);
            }
            this._playZoomSlideMedia(swiper.activeIndex);
          },
        },
      });

      this._initZoomPan();

      // Double rAF: zoom swiper assigned + modal fully painted before play
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this._playZoomSlideMedia(this.zoomSwiperInstance.activeIndex));
      });

      // Keyboard navigation
      this._handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          this.closeZoom();
        } else if (e.key === 'ArrowRight') {
          this.zoomSwiperInstance?.slideNext();
        } else if (e.key === 'ArrowLeft') {
          this.zoomSwiperInstance?.slidePrev();
        }
      };
      document.addEventListener('keydown', this._handleKeyDown);
    }

    closeZoom() {
      const resumeIndex = this.zoomSwiperInstance?.activeIndex ?? this.swiperInstance?.activeIndex ?? 0;

      const modal = this.zoomModal;
      if (modal) {
        // Unload any playing video in zoom modal
        modal.querySelectorAll('deferred-media[loaded]').forEach((dm) => {
          const video = dm.querySelector('video');
          const iframe = dm.querySelector('iframe');
          if (video) {
            video.pause();
            video.remove();
          } else if (iframe) {
            iframe.remove();
          }
          dm.removeAttribute('loaded');
        });

        modal.classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
      }

      // Clean up keyboard listeners
      if (this._handleKeyDown) {
        document.removeEventListener('keydown', this._handleKeyDown);
        this._handleKeyDown = null;
      }

      // Clean up zoom/pan listeners and reset all slides
      if (this._zoomPanAbort) {
        this._zoomPanAbort.abort();
        this._zoomPanAbort = null;
      }
      modal?.querySelectorAll('.swiper-slide').forEach((s) => s._resetZoom?.());

      // Destroy zoom swiper — must be null before _playSlideMedia (guard check)
      if (this.zoomSwiperInstance) {
        this.zoomSwiperInstance.destroy(true, true);
        this.zoomSwiperInstance = null;
      }

      // Resume main gallery at the same slide
      requestAnimationFrame(() => this._playSlideMedia(resumeIndex));
    }

    _initZoomPan() {
      const modal = this.zoomModal;
      if (!modal) return;

      if (this._zoomPanAbort) this._zoomPanAbort.abort();
      this._zoomPanAbort = new AbortController();
      const { signal } = this._zoomPanAbort;

      modal.querySelectorAll('.swiper-slide').forEach((slide) => {
        const img = slide.querySelector('img');
        if (!img) return;

        let scale = 1;
        let tx = 0;
        let ty = 0;
        let dragging = false;
        let lastMX = 0;
        let lastMY = 0;
        let pinchDist0 = 0;
        let pinchScale0 = 1;
        let lastTouchX = 0;
        let lastTouchY = 0;

        img.style.transformOrigin = 'center center';
        img.style.willChange = 'transform';

        const apply = () => {
          img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
          if (this.zoomSwiperInstance) this.zoomSwiperInstance.allowTouchMove = scale <= 1;
          img.style.cursor = scale > 1 ? 'grab' : '';
        };

        const clamp = () => {
          if (scale <= 1) { tx = 0; ty = 0; return; }
          const maxX = (img.offsetWidth * (scale - 1)) / 2;
          const maxY = (img.offsetHeight * (scale - 1)) / 2;
          tx = Math.max(-maxX, Math.min(maxX, tx));
          ty = Math.max(-maxY, Math.min(maxY, ty));
        };

        slide._resetZoom = () => {
          scale = 1; tx = 0; ty = 0;
          img.style.transform = '';
          img.style.cursor = '';
          if (this.zoomSwiperInstance) this.zoomSwiperInstance.allowTouchMove = true;
        };

        // Wheel zoom (desktop)
        slide.addEventListener('wheel', (e) => {
          e.preventDefault();
          const step = e.deltaY < 0 ? 0.15 : -0.15;
          const newScale = Math.max(1, Math.min(5, scale + step));
          if (newScale === scale) return;

          if (newScale === 1) {
            scale = 1; tx = 0; ty = 0;
          } else {
            const rect = slide.getBoundingClientRect();
            const cx = e.clientX - rect.left - rect.width / 2;
            const cy = e.clientY - rect.top - rect.height / 2;
            const ratio = newScale / scale;
            tx = cx * (1 - ratio) + tx * ratio;
            ty = cy * (1 - ratio) + ty * ratio;
            scale = newScale;
          }
          clamp();
          apply();
        }, { passive: false, signal });

        // Mouse drag (desktop)
        slide.addEventListener('mousedown', (e) => {
          if (scale <= 1) return;
          e.preventDefault();
          dragging = true;
          lastMX = e.clientX;
          lastMY = e.clientY;
          img.style.cursor = 'grabbing';
        }, { signal });

        window.addEventListener('mousemove', (e) => {
          if (!dragging) return;
          tx += e.clientX - lastMX;
          ty += e.clientY - lastMY;
          lastMX = e.clientX;
          lastMY = e.clientY;
          clamp();
          apply();
        }, { signal });

        window.addEventListener('mouseup', () => {
          if (!dragging) return;
          dragging = false;
          img.style.cursor = scale > 1 ? 'grab' : '';
        }, { signal });

        // Pinch + pan (mobile)
        slide.addEventListener('touchstart', (e) => {
          if (e.touches.length === 2) {
            const [t0, t1] = e.touches;
            pinchDist0 = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
            pinchScale0 = scale;
          } else if (e.touches.length === 1 && scale > 1) {
            lastTouchX = e.touches[0].clientX;
            lastTouchY = e.touches[0].clientY;
          }
        }, { passive: true, signal });

        slide.addEventListener('touchmove', (e) => {
          if (e.touches.length === 2) {
            e.preventDefault();
            const [t0, t1] = e.touches;
            const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
            scale = Math.max(1, Math.min(5, pinchScale0 * (dist / pinchDist0)));
            if (scale <= 1) { scale = 1; tx = 0; ty = 0; }
            clamp();
            apply();
          } else if (e.touches.length === 1 && scale > 1) {
            e.preventDefault();
            tx += e.touches[0].clientX - lastTouchX;
            ty += e.touches[0].clientY - lastTouchY;
            lastTouchX = e.touches[0].clientX;
            lastTouchY = e.touches[0].clientY;
            clamp();
            apply();
          }
        }, { passive: false, signal });

        // Sync pan origin when releasing one finger after pinch (prevents snap on 2→1 touch transition)
        slide.addEventListener('touchend', (e) => {
          if (e.touches.length === 1 && scale > 1) {
            lastTouchX = e.touches[0].clientX;
            lastTouchY = e.touches[0].clientY;
          }
        }, { passive: true, signal });

        // Double-click/tap to reset zoom
        slide.addEventListener('dblclick', () => slide._resetZoom?.(), { signal });
      });

      // Reset zoom on slide change
      if (this.zoomSwiperInstance) {
        this.zoomSwiperInstance.on('slideChange', () => {
          modal.querySelectorAll('.swiper-slide').forEach((s) => s._resetZoom?.());
        });
      }
    }
  });
}
